/* ═══════════════════════════════════════════════════════════════
   AMBIENT PARTICLES — slow, sparse gold motes drifting up over the
   interior sky background (register / verify / upload / sign-in /
   pre- & post-program / error / sweeps-limit). NOT on the thank-you
   page (its intro + video sequence owns that moment).

   Self-contained: builds its own <canvas> into each `.int-body`,
   styled inline, so it needs no page CSS. Sits above the sky (z0)
   and blimp accents, below the page content/card (z3).

   Fully optional + safe: set ENABLE_AMBIENT_PARTICLES = false to
   disable. Skipped entirely under prefers-reduced-motion. Paused
   while its section is scrolled out of view.
   ═══════════════════════════════════════════════════════════════ */
(function(){
  const ENABLE_AMBIENT_PARTICLES = true;            // ← flip to false to disable
  if (!ENABLE_AMBIENT_PARTICLES) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const COLORS = ['255,224,140', '255,238,196', '255,210,120', '236,214,255'];

  function initField(host){
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    const canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText = 'position:absolute;inset:0;z-index:2;pointer-events:none;width:100%;height:100%;';
    host.appendChild(canvas);
    const ctx = canvas.getContext('2d');

    let W = 0, H = 0, dpr = Math.min(window.devicePixelRatio || 1, 2);
    let parts = [];
    let running = false, raf = null;

    function build(){
      const r = host.getBoundingClientRect();
      W = Math.max(1, r.width); H = Math.max(1, r.height);
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const target = Math.round(Math.min(Math.max(W / 26, 14), 46));
      parts = [];
      for (let i = 0; i < target; i++) parts.push(spawn(true));
    }
    function spawn(anywhere){
      return {
        x: Math.random() * W,
        y: anywhere ? Math.random() * H : H + 10,
        r: 0.7 + Math.random() * 1.8,
        vy: 0.08 + Math.random() * 0.34,
        drift: (Math.random() * 2 - 1) * 0.28,
        phase: Math.random() * Math.PI * 2,
        tw: 0.006 + Math.random() * 0.014,
        base: 0.16 + Math.random() * 0.34,
        color: COLORS[(Math.random() * COLORS.length) | 0]
      };
    }
    function frame(){
      if (!running) return;
      ctx.clearRect(0, 0, W, H);
      for (const p of parts){
        p.y -= p.vy;
        p.phase += p.tw;
        p.x += p.drift + Math.sin(p.phase) * 0.16;
        if (p.y < -10 || p.x < -20 || p.x > W + 20){ Object.assign(p, spawn(false)); continue; }
        const op = p.base * (0.6 + 0.4 * Math.sin(p.phase));
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(' + p.color + ',' + op.toFixed(3) + ')';
        ctx.shadowColor = 'rgba(' + p.color + ',' + (op * 0.8).toFixed(3) + ')';
        ctx.shadowBlur = p.r * 3.5;
        ctx.fill();
      }
      ctx.shadowBlur = 0;
      raf = requestAnimationFrame(frame);
    }
    function start(){ if (running) return; running = true; raf = requestAnimationFrame(frame); }
    function stop(){ running = false; if (raf) cancelAnimationFrame(raf); raf = null; }

    build();
    let rt = null;
    window.addEventListener('resize', function(){ clearTimeout(rt); rt = setTimeout(build, 220); });

    if ('IntersectionObserver' in window){
      new IntersectionObserver(function(ents){
        ents.forEach(function(e){ e.isIntersecting ? start() : stop(); });
      }, { threshold: 0 }).observe(host);
    } else { start(); }
  }

  const initializedFields = new WeakSet();

  function initFieldSafe(host) {
    if (!host || initializedFields.has(host)) return;
    initializedFields.add(host);
    initField(host);
  }

  function boot() {
    document.querySelectorAll('.int-body').forEach(initFieldSafe);
  }

  function watchForNewBodies() {
    if (!('MutationObserver' in window)) return;
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.matches('.int-body')) initFieldSafe(node);
          node.querySelectorAll && node.querySelectorAll('.int-body').forEach(initFieldSafe);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  window.ambientParticles = window.ambientParticles || {};
  window.ambientParticles.boot = boot;
  window.ambientParticles.watchForNewBodies = watchForNewBodies;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      boot();
      watchForNewBodies();
    });
  } else {
    boot();
    watchForNewBodies();
  }
})();

/* ═══════════════════════════════════════════════════════════════
   (E) DUST SETTLE — a faint puff of golden dust settles ONCE as each
   scenic section first enters view, then stops. Not a loop; one-shot
   per section, self-removing. Toggle: ENABLE_DUST_SETTLE.
   ═══════════════════════════════════════════════════════════════ */
(function () {
  const ENABLE_DUST_SETTLE = true;             // ← flip to false to disable
  if (!ENABLE_DUST_SETTLE) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const COLORS = ['255,224,140', '255,246,216', '226,206,250']; // gold, warm white, faint lavender
  const ids = ['grand-prize', 'how-to-enter', 'product', 'secondary-prize'];
  const io = new IntersectionObserver(entries => {
    entries.forEach(e => { if (e.isIntersecting) { spawn(e.target); io.unobserve(e.target); } });
  }, { threshold: 0.18 });
  ids.forEach(id => { const el = document.getElementById(id); if (el) io.observe(el); });

  function spawn(section) {
    const layer = document.createElement('div');
    layer.className = 'dust-layer';
    layer.setAttribute('aria-hidden', 'true');
    section.appendChild(layer);
    const N = 24;
    let maxLife = 0;
    for (let i = 0; i < N; i++) {
      const m = document.createElement('span');
      m.className = 'dust-mote';
      const r = 1.4 + Math.random() * 3;
      const dur = 1.9 + Math.random() * 1.5;
      const delay = Math.random() * 0.55;
      maxLife = Math.max(maxLife, dur + delay);
      m.style.left = (4 + Math.random() * 92) + '%';
      m.style.width = r + 'px';
      m.style.height = r + 'px';
      m.style.background = 'rgba(' + COLORS[(Math.random() * COLORS.length) | 0] + ',1)';
      m.style.boxShadow = '0 0 ' + (r * 3.6).toFixed(1) + 'px rgba(255,226,150,0.6)';
      m.style.setProperty('--op', (0.5 + Math.random() * 0.4).toFixed(2));
      m.style.setProperty('--fall', (70 + Math.random() * 150).toFixed(0) + 'px');
      m.style.setProperty('--sway', ((Math.random() - 0.5) * 50).toFixed(0) + 'px');
      m.style.setProperty('--dur', dur.toFixed(2) + 's');
      m.style.setProperty('--delay', delay.toFixed(2) + 's');
      layer.appendChild(m);
    }
    setTimeout(() => layer.remove(), (maxLife * 1000) + 400);
  }
})();
